import React, { useState } from "react";
import { Link } from "react-router-dom";

/* =========================================================
   ENGINEERING PROFILE
   ========================================================= */

const engineeringProfile = [
  {
    name: "AWS",
    label: "Compute & cloud",
    icon: "devicon-amazonwebservices-plain-wordmark",
    color: "#FF9900",
  },
  {
    name: "GCP",
    label: "Analytics & AI",
    icon: "devicon-googlecloud-plain",
    color: "#4285F4",
  },
  {
    name: "Python",
    label: "Python / Spark",
    icon: "devicon-python-plain",
    color: "#3776AB",
  },
  {
    name: "AI",
    label: "Intelligent systems",
    icon: "devicon-tensorflow-original",
    color: "#A78BFA",
  },
  {
    name: "LinkedIn",
    label: "Professional network",
    icon: "devicon-linkedin-plain",
    color: "#0A66C2",
    href: "https://www.linkedin.com/in/stephen-adegbile-663537138/",
  },
  {
    name: "GitHub",
    label: "Code & projects",
    icon: "devicon-github-original",
    color: "#EDEFF2",
    href: "https://github.com/Stephen0111",
  },
];

/* =========================================================
   THE PERSON BEHIND THE PIPELINES
   Edit the copy freely: add a favourite player, album, museum, aircraft...
   `work` is the line that ties each interest back to how I engineer.
   ========================================================= */

const lifeChips = [
  { label: "Chelsea FC", color: "#4285F4" },
  { label: "Music", color: "#A78BFA" },
  { label: "Art", color: "#E3A857" },
  { label: "History", color: "#E3A857" },
  { label: "Aviation", color: "#4CD8B8" },
];

const interests = [
  {
    key: "football",
    title: "Football & Chelsea FC",
    tag: "Blue through and through",
    text: "Chelsea through the highs, the lows and everything in between. Football is where I learned that great teams beat great individuals, and that the post-match debate is half the fun.",
    work: " The best platforms, like the best sides, are built together.",
    color: "#4285F4",
    span: "lg:col-span-3",
  },
  {
    key: "music",
    title: "Music",
    tag: "Always a playlist running",
    text: "For focus, for the commute, for pure joy. Different moods, different soundtracks, and I'm always hunting for the next thing to add.",
    work: "Rhythm and structure. A good pipeline, like a good track, has a flow you can feel.",
    color: "#A78BFA",
    span: "lg:col-span-3",
  },
  {
    key: "art",
    title: "Art",
    tag: "An eye for detail",
    text: "I love how a single image can say what paragraphs can't, and how much thought hides behind something that looks effortless.",
    work: "Making complex things feel clear, and sweating the small details.",
    color: "#E3A857",
    span: "lg:col-span-2",
  },
  {
    key: "history",
    title: "History",
    tag: "Why things are the way they are",
    text: "Empires, inventions, turning points. I'm endlessly curious about how the past explains the present, and what we keep repeating.",
    work: "Context matters. Every legacy system has a story worth reading before you change it.",
    color: "#E3A857",
    span: "lg:col-span-2",
  },
  {
    key: "jets",
    title: "Private jets",
    tag: "Head in the clouds",
    text: "Fascinated by aviation, from the engineering and design to the experience of the cabin door closing behind you. One day, perhaps.",
    work: "Aviation runs on checklists and redundancy: the same mindset I bring to reliable data systems.",
    color: "#4CD8B8",
    span: "lg:col-span-2",
  },
];

/* =========================================================
   ARCHITECTURE DIAGRAM DATA
   ========================================================= */

const TOOL_ICONS = {
  S3: { icon: "devicon-amazonwebservices-plain-wordmark", color: "#FF9900" },
  GCS: { icon: "devicon-googlecloud-plain", color: "#4285F4" },
  Python: { icon: "devicon-python-plain", color: "#3776AB" },
  Spark: { icon: "devicon-apachespark-plain", color: "#E25A1C" },
  BigQuery: { icon: "devicon-googlecloud-plain", color: "#4285F4" },
  PostgreSQL: { icon: "devicon-postgresql-plain", color: "#5B8FC9" },
  FastAPI: { icon: "devicon-fastapi-plain", color: "#05A28F" },
  "Cloud Run": { icon: "devicon-googlecloud-plain", color: "#4285F4" },
  React: { icon: "devicon-react-original", color: "#61DAFB" },
  "Vertex AI": { icon: "devicon-googlecloud-plain", color: "#4285F4" },
  Airflow: { icon: "devicon-apacheairflow-plain", color: "#2FA3F2" },
  Terraform: { icon: "devicon-terraform-plain", color: "#8C5CF0" },
  Docker: { icon: "devicon-docker-plain", color: "#2496ED" },
  Git: { icon: "devicon-git-plain", color: "#F05032" },
};

const layers = [
  {
    id: "L1",
    name: "Ingest",
    sub: "Sources to a raw landing zone",
    color: "#FF9900",
    stack: ["S3", "GCS", "APIs", "Batch", "Events"],
    detail:
      "Pulling data from APIs, files and operational systems into a durable raw landing zone on S3 or GCS, so every downstream layer can be rebuilt from source.",
  },
  {
    id: "L2",
    name: "Process",
    sub: "Clean, conform, enrich",
    color: "#E25A1C",
    stack: ["Python", "Spark", "SQL"],
    detail:
      "Distributed transformation that standardises, validates and enriches data. Jobs are idempotent and testable, so a re-run is always safe.",
  },
  {
    id: "L3",
    name: "Model & store",
    sub: "Curated, trusted datasets",
    color: "#4285F4",
    stack: ["BigQuery", "PostgreSQL"],
    detail:
      "Analytical and operational models with consistent definitions, designed for query performance and cost, not just for storage.",
  },
  {
    id: "L4",
    name: "Serve",
    sub: "APIs and data products",
    color: "#4CD8B8",
    stack: ["FastAPI", "Cloud Run", "React"],
    detail:
      "Trusted data exposed through APIs and interfaces that people and other systems can depend on.",
  },
  {
    id: "L5",
    name: "Intelligence",
    sub: "AI on governed data",
    color: "#A78BFA",
    stack: ["Vertex AI", "LLMs", "RAG"],
    detail:
      "ML and LLM systems such as retrieval and agents, grounded in the curated layers below rather than in raw, unchecked data.",
  },
];

const rails = [
  {
    name: "Orchestration",
    tools: ["Airflow"],
    tech: "Airflow",
    text: "Dependency-driven scheduling, retries and backfills.",
    color: "#4CD8B8",
  },
  {
    name: "Infrastructure",
    tools: ["Terraform", "Docker", "Git"],
    tech: "Terraform · Docker · Git",
    text: "Reproducible environments, version-controlled end to end.",
    color: "#FF9900",
  },
  {
    name: "Quality & observability",
    tech: "Tests · Monitoring",
    text: "Pipelines you can see into, with failures caught early.",
    color: "#4285F4",
  },
  {
    name: "Security & governance",
    tech: "Access control · Lineage",
    text: "The right people see the right data, with a clear trail.",
    color: "#A78BFA",
  },
];

/* =========================================================
   TECHNICAL SCHEMA
   ========================================================= */

const schema = [
  {
    category: "Cloud platforms",

    fields: [
      {
        name: "AWS",
        value: "Compute · Storage · Data Infrastructure",
        description:
          "Using AWS for cloud infrastructure, compute workloads, storage, data platforms and production engineering.",
      },
      {
        name: "GCP",
        value: "BigQuery · Vertex AI · Cloud Run",
        description:
          "Using Google Cloud primarily for analytics and AI workloads, including analytical data platforms and intelligent applications.",
      },
    ],
  },

  {
    category: "Languages & frameworks",

    fields: [
      {
        name: "Python",
        value: "Data Engineering · APIs · AI",
        description:
          "Primary engineering language for data pipelines, automation, APIs, distributed processing and AI systems.",
      },
      {
        name: "FastAPI",
        value: "Cloud-native APIs",
        description:
          "Building lightweight APIs and backend services for data platforms and intelligent applications.",
      },
      {
        name: "React",
        value: "Modern interfaces",
        description:
          "Building modern interfaces for technical platforms, dashboards and intelligent applications.",
      },
      {
        name: "Node.js",
        value: "Backend systems",
        description:
          "Developing backend services, integrations and application infrastructure.",
      },
    ],
  },

  {
    category: "Data & orchestration",

    fields: [
      {
        name: "SQL",
        value: "Analytics · Transformation",
        description:
          "Working with relational and analytical data stores to transform, model and serve reliable data.",
      },
      {
        name: "Apache Spark",
        value: "Distributed Processing",
        description:
          "Using distributed processing patterns for larger-scale data transformation and engineering workloads.",
      },
      {
        name: "Airflow",
        value: "Workflow Orchestration",
        description:
          "Designing dependency-driven workflows for reliable and repeatable data pipelines.",
      },
      {
        name: "PostgreSQL",
        value: "Relational Data",
        description:
          "Working with reliable relational databases for operational systems and application backends.",
      },
    ],
  },

  {
    category: "Infrastructure & tooling",

    fields: [
      {
        name: "Terraform",
        value: "Infrastructure as Code",
        description:
          "Defining reproducible cloud infrastructure through version-controlled configuration.",
      },
      {
        name: "Docker",
        value: "Containerisation",
        description:
          "Packaging applications and services consistently for local and cloud deployment.",
      },
      {
        name: "Git",
        value: "Version Control",
        description:
          "Managing source code, infrastructure and collaborative engineering workflows.",
      },
    ],
  },
];

/* =========================================================
   SHARED BITS
   ========================================================= */

function SectionHeading({ eyebrow, title, text, className = "mb-7" }) {
  return (
    <div className={className}>
      <div className="flex items-center gap-3 text-sm font-medium text-[#4CD8B8]">
        <span className="h-px w-7 bg-[#4CD8B8]" />
        {eyebrow}
      </div>

      <h2 className="mt-3 text-xl font-semibold tracking-tight text-white sm:text-3xl">
        {title}
      </h2>

      {text && (
        <p className="mt-3 max-w-2xl text-sm leading-7 text-white/45 sm:text-[15px]">
          {text}
        </p>
      )}
    </div>
  );
}

function InterestIcon({ name, size = 22 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (name) {
    case "football":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="m12 8.2 3.4 2.5-1.3 4h-4.2l-1.3-4z" />
          <path d="M12 8.2V3.2M15.4 10.7l4.7-1.6M14.1 14.7l2.9 4M9.9 14.7l-2.9 4M8.6 10.7 3.9 9.1" />
        </svg>
      );
    case "music":
      return (
        <svg {...common}>
          <path d="M9 18V5l11-2v13" />
          <circle cx="6.5" cy="18" r="2.5" />
          <circle cx="17.5" cy="16" r="2.5" />
        </svg>
      );
    case "art":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="2.5" />
          <circle cx="9" cy="10" r="1.6" />
          <path d="m3.5 17 5-4.5 4 3.5 3-2.5 5 4" />
        </svg>
      );
    case "history":
      return (
        <svg {...common}>
          <path d="M3 21h18" />
          <path d="M5 21V10M9.5 21V10M14.5 21V10M19 21V10" />
          <path d="M3 10 12 4l9 6z" />
        </svg>
      );
    case "jets":
      return (
        <svg {...common}>
          <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
        </svg>
      );
    default:
      return null;
  }
}

/* =========================================================
   BEYOND THE CODE
   ========================================================= */

function BeyondTheCode() {
  return (
    <section className="mt-14 sm:mt-20">
      <SectionHeading
        eyebrow="Beyond the code"
        title="The person behind the pipelines"
        text="Work is only part of the picture. These are the things I'd talk about for hours, and the ways they quietly shape how I build."
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-6">
        {interests.map((item) => (
          <article
            key={item.key}
            className={`
              group relative flex flex-col overflow-hidden
              rounded-2xl border border-white/10 bg-white/[0.025]
              p-5 transition-all duration-500
              hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.045]
              sm:p-6
              ${item.key === "jets" ? "sm:col-span-2" : ""} ${item.span}
            `}
          >
            <div
              className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full opacity-[0.08] blur-3xl transition-all duration-700 group-hover:scale-150 group-hover:opacity-25"
              style={{ backgroundColor: item.color }}
            />

            <div className="relative flex items-start gap-4">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border transition-transform duration-500 group-hover:scale-110"
                style={{
                  color: item.color,
                  borderColor: `${item.color}40`,
                  backgroundColor: `${item.color}12`,
                }}
              >
                <InterestIcon name={item.key} />
              </div>

              <div className="min-w-0">
                <h3 className="text-base font-semibold text-white">
                  {item.title}
                </h3>
                <p
                  className="mt-0.5 text-xs font-medium"
                  style={{ color: item.color }}
                >
                  {item.tag}
                </p>
              </div>
            </div>

            <p className="relative mt-4 text-sm leading-7 text-white/50">
              {item.text}
            </p>

            <div className="relative mt-auto pt-5">
              <div className="rounded-xl border border-white/[0.07] bg-black/20 px-4 py-3">
                <p
                  className="text-[11px] font-medium"
                  style={{ color: item.color }}
                >
                  How it shows up in my work
                </p>
                <p className="mt-1 text-[13px] leading-6 text-white/55">
                  {item.work}
                </p>
              </div>
            </div>

            <div
              className="absolute bottom-0 left-0 h-[2px] w-0 transition-all duration-500 group-hover:w-full"
              style={{ backgroundColor: item.color }}
            />
          </article>
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   ENGINEERING PROFILE COMPONENT
   ========================================================= */

function EngineeringProfile() {
  return (
    <section className="mt-14 sm:mt-20">
      <SectionHeading
        eyebrow="The toolkit"
        title="The systems I work with"
        className="mb-6"
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {engineeringProfile.map((item) => {
          const Card = item.href ? "a" : "div";

          return (
            <Card
              key={item.name}
              href={item.href}
              target={item.href ? "_blank" : undefined}
              rel={item.href ? "noreferrer" : undefined}
              className="
                group relative overflow-hidden
                rounded-2xl
                border border-white/10
                bg-white/[0.025]
                p-4 sm:p-5
                transition-all duration-500
                hover:-translate-y-1
                hover:border-white/20
                hover:bg-white/[0.05]
              "
            >
              {/* Ambient glow */}
              <div
                className="
                  pointer-events-none
                  absolute -right-10 -top-10
                  h-28 w-28
                  rounded-full
                  opacity-[0.07]
                  blur-3xl
                  transition-all duration-700
                  group-hover:scale-[1.8]
                  group-hover:opacity-20
                "
                style={{
                  backgroundColor: item.color,
                }}
              />

              {/* Icon */}
              <div
                className="
                  relative
                  flex h-14 w-14 sm:h-16 sm:w-16
                  items-center justify-center
                  rounded-2xl
                  border border-white/10
                  bg-black/25
                  transition-all duration-500
                  group-hover:scale-110
                "
              >
                <i
                  className={`${item.icon} text-[2rem] transition-all duration-500 sm:text-[2.35rem]`}
                  style={{
                    color: item.color,
                    filter: `drop-shadow(0 0 10px ${item.color}45)`,
                  }}
                />
              </div>

              {/* Content */}
              <div className="relative mt-4 sm:mt-5">
                <h3 className="text-sm font-semibold text-white">
                  {item.name}
                </h3>

                <p className="mt-1 text-xs text-white/40">{item.label}</p>
              </div>

              {/* Bottom hover line */}
              <div
                className="
                  absolute bottom-0 left-0
                  h-[2px] w-0
                  transition-all duration-500
                  group-hover:w-full
                "
                style={{
                  backgroundColor: item.color,
                }}
              />
            </Card>
          );
        })}
      </div>
    </section>
  );
}

/* =========================================================
   LAYERED ARCHITECTURE DIAGRAM
   A reference data platform: five layers from raw source to
   intelligence, with the cross-cutting concerns that make it
   production-grade. Tap (or hover) a layer to see what it does.
   ========================================================= */

function EngineeringGraph() {
  const [active, setActive] = useState(0);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#090C11]">
      {/* Technical grid */}
      <div
        className="
          pointer-events-none
          absolute inset-0
          opacity-[0.08]
          [background-image:linear-gradient(rgba(255,255,255,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.12)_1px,transparent_1px)]
          [background-size:36px_36px]
        "
      />

      {/* Ambient glow */}
      <div className="pointer-events-none absolute -right-20 top-10 h-64 w-64 rounded-full bg-[#A78BFA]/10 blur-[100px]" />
      <div className="pointer-events-none absolute -left-20 bottom-20 h-64 w-64 rounded-full bg-[#4CD8B8]/8 blur-[100px]" />

      {/* Header */}
      <div className="relative flex items-center justify-between gap-3 border-b border-white/[0.07] px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <p className="text-[11px] text-white/35">A reference architecture</p>
          <p className="mt-0.5 truncate text-sm font-medium text-[#4CD8B8]">
            How I design a data platform
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
          <i className="devicon-amazonwebservices-plain-wordmark text-base text-[#FF9900]" />
          <span className="font-mono text-[9px] text-white/25">+</span>
          <i className="devicon-googlecloud-plain text-sm text-[#4285F4]" />
        </div>
      </div>

      <div className="relative p-4 sm:p-6">
        <p className="mb-4 text-xs text-white/30">
          Data flows down. Tap a layer to see what it does.
        </p>

        {/* Layers */}
        <div>
          {layers.map((layer, index) => {
            const isActive = active === index;
            const isLast = index === layers.length - 1;

            return (
              <div key={layer.id} className="flex gap-3 sm:gap-4">
                {/* Spine */}
                <div className="flex w-9 shrink-0 flex-col items-center">
                  <div
                    className="relative flex h-9 w-9 items-center justify-center rounded-full border font-mono text-[10px] font-semibold transition-all duration-300"
                    style={{
                      color: layer.color,
                      borderColor: `${layer.color}${isActive ? "99" : "40"}`,
                      background: `radial-gradient(circle, ${layer.color}${
                        isActive ? "30" : "12"
                      }, rgba(8,11,16,.96) 70%)`,
                      boxShadow: isActive
                        ? `0 0 24px ${layer.color}35`
                        : "none",
                    }}
                  >
                    {layer.id}
                  </div>

                  {!isLast && (
                    <div className="relative my-1 w-px flex-1 bg-white/10">
                      <span
                        className="ab-packet"
                        style={{
                          background: layer.color,
                          boxShadow: `0 0 10px ${layer.color}`,
                          animationDelay: `${index * 0.7}s`,
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Layer card */}
                <div className={`min-w-0 flex-1 ${isLast ? "" : "pb-3"}`}>
                  <button
                    type="button"
                    onClick={() => setActive(isActive ? -1 : index)}
                    onMouseEnter={() => setActive(index)}
                    aria-expanded={isActive}
                    className="w-full rounded-2xl border bg-white/[0.02] px-4 py-3.5 text-left transition-all duration-300 hover:bg-white/[0.04] sm:px-5"
                    style={{
                      borderColor: isActive
                        ? `${layer.color}55`
                        : "rgba(255,255,255,0.1)",
                      boxShadow: isActive
                        ? `0 0 40px ${layer.color}12`
                        : "none",
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-white sm:text-base">
                          {layer.name}
                        </h3>
                        <p className="mt-0.5 text-xs text-white/35">
                          {layer.sub}
                        </p>
                      </div>

                      <span
                        className={`shrink-0 text-lg leading-none text-white/25 transition-transform duration-300 ${
                          isActive ? "rotate-45" : ""
                        }`}
                      >
                        +
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {layer.stack.map((tool) => {
                        const toolIcon = TOOL_ICONS[tool];

                        return (
                          <span
                            key={tool}
                            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] tracking-wide"
                            style={{
                              color: layer.color,
                              borderColor: `${layer.color}30`,
                              backgroundColor: `${layer.color}0D`,
                            }}
                          >
                            {toolIcon && (
                              <i
                                className={`${toolIcon.icon} text-[13px]`}
                                style={{ color: toolIcon.color }}
                              />
                            )}
                            {tool}
                          </span>
                        );
                      })}
                    </div>

                    <div
                      className={`grid transition-all duration-300 ${
                        isActive
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="pt-3 text-xs leading-6 text-white/50">
                          {layer.detail}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Cross-cutting concerns */}
        <div className="mt-6 rounded-2xl border border-dashed border-white/15 p-3 sm:p-4">
          <p className="mb-3 px-1 text-xs text-white/35">Across every layer</p>

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            {rails.map((rail) => (
              <div
                key={rail.name}
                className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{
                      backgroundColor: rail.color,
                      boxShadow: `0 0 8px ${rail.color}`,
                    }}
                  />
                  <h4 className="min-w-0 text-xs font-semibold text-white">
                    {rail.name}
                  </h4>

                  {rail.tools && (
                    <div className="ml-auto flex shrink-0 items-center gap-1.5">
                      {rail.tools.map((tool) => (
                        <i
                          key={tool}
                          className={`${TOOL_ICONS[tool].icon} text-base`}
                          style={{ color: TOOL_ICONS[tool].color }}
                          title={tool}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <p
                  className="mt-1.5 font-mono text-[10px]"
                  style={{ color: rail.color }}
                >
                  {rail.tech}
                </p>

                <p className="mt-1.5 text-[11px] leading-5 text-white/40">
                  {rail.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative flex items-center justify-between gap-3 border-t border-white/[0.07] px-4 py-3 text-xs sm:px-6">
        <span className="text-white/30">Source → trust → insight</span>

        <span className="flex shrink-0 items-center gap-2 text-[#4CD8B8]/70">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4CD8B8]" />
          Running smoothly
        </span>
      </div>
    </div>
  );
}

/* =========================================================
   ENGINEERING SCHEMA
   ========================================================= */

function SchemaSection() {
  const [active, setActive] = useState(null);

  return (
    <section className="mt-14 sm:mt-20">
      <SectionHeading
        eyebrow="The detail"
        title="How the stack fits together"
        text="A practical engineering stack spanning cloud infrastructure, analytics, software engineering, distributed processing, orchestration and AI."
      />

      <div className="space-y-3">
        {schema.map((group) => (
          <div
            key={group.category}
            className="
              overflow-hidden
              rounded-2xl
              border border-white/10
              bg-white/[0.02]
            "
          >
            {/* Category */}
            <div className="border-b border-white/5 px-4 py-3.5 text-xs font-medium text-white/35 sm:px-5">
              {group.category}
            </div>

            {/* Fields */}
            <div className="divide-y divide-white/5">
              {group.fields.map((field) => {
                const isActive = active === field.name;

                return (
                  <button
                    key={field.name}
                    type="button"
                    onClick={() => setActive(isActive ? null : field.name)}
                    aria-expanded={isActive}
                    className="
                      group
                      w-full
                      text-left
                      transition-colors
                      hover:bg-white/[0.025]
                    "
                  >
                    <div className="flex items-center justify-between gap-4 px-4 py-4 sm:gap-5 sm:px-5">
                      <div className="flex min-w-0 items-center gap-4">
                        <span
                          className="
                            h-1.5 w-1.5 shrink-0
                            rounded-full
                            bg-[#4CD8B8]
                            opacity-50
                            transition-all
                            group-hover:scale-150
                            group-hover:opacity-100
                          "
                        />

                        <div className="min-w-0">
                          <div className="font-mono text-sm text-white">
                            {field.name}
                          </div>

                          <div className="mt-1 text-xs text-white/35">
                            {field.value}
                          </div>
                        </div>
                      </div>

                      <div
                        className={`
                          shrink-0
                          text-lg
                          text-white/20
                          transition-transform duration-300
                          ${isActive ? "rotate-45" : ""}
                        `}
                      >
                        +
                      </div>
                    </div>

                    {/* Description */}
                    <div
                      className={`
                        grid
                        transition-all
                        duration-300
                        ${
                          isActive
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0"
                        }
                      `}
                    >
                      <div className="overflow-hidden">
                        <p
                          className="
                            px-4
                            pb-5
                            pl-[2.6rem]
                            sm:px-5
                            sm:pl-[3.1rem]
                            text-xs
                            leading-6
                            text-white/45
                          "
                        >
                          {field.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   MAIN ABOUT PAGE
   ========================================================= */

const principles = [
  {
    title: "Reliable",
    text: "Designing data flows that are observable, reproducible and resilient.",
    color: "#4CD8B8",
  },
  {
    title: "Practical",
    text: "Choosing technology because it solves the problem, not because it is fashionable.",
    color: "#E3A857",
  },
  {
    title: "Intelligent",
    text: "Using AI where it genuinely improves how systems understand and act on data.",
    color: "#A78BFA",
  },
];

export default function About() {
  return (
    <main className="min-h-screen overflow-x-hidden bg-[#080B10] text-[#EDEFF2]">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute left-[10%] top-[8%] h-80 w-80 rounded-full bg-[#4CD8B8]/5 blur-[130px]" />

        <div className="absolute right-[5%] top-[35%] h-96 w-96 rounded-full bg-[#A78BFA]/5 blur-[150px]" />

        <div className="absolute bottom-[10%] left-[45%] h-72 w-72 rounded-full bg-[#4285F4]/5 blur-[130px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-10">
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end lg:gap-10">
          <div>
            <div className="mb-5 flex items-center gap-3 text-sm font-medium text-[#4CD8B8] sm:mb-6">
              <span className="h-px w-8 bg-[#4CD8B8]" />
              About me
            </div>

            <h1
              className="
                max-w-xl
                text-[2.6rem]
                font-semibold
                leading-[1]
                tracking-[-0.04em]
                text-white
                sm:text-6xl
              "
              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
            >
              Engineer by trade.
              <br />
              <span className="text-white/25">Curious by nature.</span>
            </h1>
          </div>

          <div className="max-w-2xl lg:justify-self-end">
            <p className="text-[15px] leading-7 text-white/55 sm:text-lg sm:leading-8">
              I'm Stephen, a cloud data engineer working at the intersection of{" "}
              <span className="text-white">data</span>,{" "}
              <span className="text-white">cloud infrastructure</span> and{" "}
              <span className="text-white">AI</span>, building systems that
              move, transform and make sense of data reliably.
            </p>

            <p className="mt-4 text-[15px] leading-7 text-white/55 sm:text-lg sm:leading-8">
              Away from the screen I'm a{" "}
              <span className="text-[#4285F4]">Chelsea</span> supporter, a music
              lover, and someone who loses hours to{" "}
              <span className="text-[#E3A857]">art and history</span> and
              anything with <span className="text-[#4CD8B8]">wings</span>.
            </p>

            {/* Work + life chips */}
            <div className="mt-6 flex flex-wrap gap-2">
              {["Data", "Cloud", "Analytics", "AI", "Automation"].map(
                (item) => (
                  <span
                    key={item}
                    className="rounded-full border border-white/10 bg-white/[0.025] px-3 py-1.5 text-xs text-white/45"
                  >
                    {item}
                  </span>
                ),
              )}

              {lifeChips.map((chip) => (
                <span
                  key={chip.label}
                  className="rounded-full border px-3 py-1.5 text-xs font-medium"
                  style={{
                    color: chip.color,
                    borderColor: `${chip.color}40`,
                    backgroundColor: `${chip.color}12`,
                  }}
                >
                  {chip.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            BEYOND THE CODE (personal)
        ===================================================== */}

        <BeyondTheCode />

        {/* =====================================================
            ENGINEERING PROFILE
        ===================================================== */}

        <EngineeringProfile />

        {/* =====================================================
            ARCHITECTURE
        ===================================================== */}

        <section className="mt-14 sm:mt-20">
          <SectionHeading
            eyebrow="The craft"
            title="Systems over silos."
            text="The interesting engineering problems usually live between technologies, where cloud, data, software and intelligence have to work together as one system."
          />

          <EngineeringGraph />
        </section>

        {/* =====================================================
            ENGINEERING PRINCIPLES
        ===================================================== */}

        <section className="mt-14 sm:mt-20">
          <SectionHeading eyebrow="How I work" title="Three things I hold to" />

          <div className="grid gap-3 sm:gap-4 md:grid-cols-3">
            {principles.map((item) => (
              <div
                key={item.title}
                className="
                  group
                  relative overflow-hidden
                  rounded-2xl
                  border border-white/10
                  bg-white/[0.02]
                  p-5 sm:p-6
                  transition-all duration-500
                  hover:-translate-y-1
                  hover:bg-white/[0.04]
                "
              >
                <span
                  className="block h-1 w-8 rounded-full"
                  style={{ backgroundColor: item.color }}
                />

                <h3 className="mt-5 text-lg font-semibold text-white">
                  {item.title}
                </h3>

                <p className="mt-3 text-sm leading-7 text-white/45">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            ENGINEERING SCHEMA
        ===================================================== */}

        <SchemaSection />

        {/* =====================================================
            CLOSING CTA
        ===================================================== */}

        <section className="mt-14 sm:mt-20">
          <div className="flex flex-col items-start justify-between gap-5 rounded-3xl border border-white/10 bg-gradient-to-r from-[#4CD8B8]/[0.07] via-transparent to-[#4285F4]/[0.07] p-6 sm:flex-row sm:items-center sm:p-8">
            <div>
              <h2
                className="text-xl font-semibold tracking-tight text-white sm:text-2xl"
                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              >
                Let's talk.
              </h2>
              <p className="mt-2 max-w-lg text-sm leading-7 text-white/50">
                Whether it's a data platform, a project idea, or who should
                start up front for Chelsea, I'd love to hear from you.
              </p>
            </div>

            <Link
              to="/contact"
              className="group inline-flex shrink-0 items-center justify-center gap-3 rounded-xl bg-[#4CD8B8] px-6 py-3.5 text-sm font-semibold text-[#080B10] transition duration-300 hover:scale-[1.03] hover:brightness-110"
            >
              Say hello
              <span className="transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </Link>
          </div>
        </section>

        {/* =====================================================
            FOOTER SIGNAL
        ===================================================== */}

        <div className="mt-14 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-5 text-xs text-white/30 sm:mt-20">
          <span>© {new Date().getFullYear()} Stephen Adegbile</span>

          <span className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4CD8B8]" />
            Up the Chels
          </span>
        </div>
      </div>

      {/* =====================================================
          LOCAL ANIMATION
      ===================================================== */}

      <style>{`
        .ab-packet {
          position: absolute;
          left: 50%;
          top: 0;
          width: 5px;
          height: 5px;
          margin-left: -2.5px;
          border-radius: 999px;
          animation: abFlow 2.8s linear infinite;
        }

        @keyframes abFlow {
          0% { top: -3px; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </main>
  );
}
